import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { Cookie } from '@nuxt-common/util/app/utils/cookie';
import { dispatchNavigationStoreChange } from '@nuxt-common/locator/app/utils/globalStoreChange';
import { defaultStoreName } from 'config';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';

import { SET_SDD_MESSAGE, SET_PREFERRED_STORE, FETCH_SDD_MESSAGE, FETCH_PREFERRED_STORE } from '#shared/types/actions';
import { GET_STORE_INFO, GET_STORE_OPERATION_INFO, GET_ZIP_CODE, GET_SDD_MESSAGE, GET_STORE_NAME, IS_STORE_SELECTED } from '#shared/types/getters';

import { logError } from '#shared/utils/Logger';
import { parseOperationHours } from '~/utils/commonStoreLocationUtil';
import { usePreferredStore } from '~/composables/dumb/usePreferredStore';
import { storeOperationStates, viewTypeToDeviceType } from '#shared/constants';

import { useNavUser } from './navUser';
import { useNavViewType } from './navViewType';

export function normalizePreferredStore(data) {
  const { address } = data;

  if (!address) return data;

  const { line1: addrLine1 } = address;

  return {
    ...data,
    address: {
      ...address,
      addrLine1,
    },
  };
}

async function updatePreferredStoreCookies(newStore, forceUpdate) {
  const is365Customer = Cookie.get('Login365Customer', 'MISCGCs') === 'Y';
  const expiryExtension = is365Customer ? 31556952000 : 2592000000;
  const cookieExpiry = new Date().getTime() + expiryExtension;
  const expires = new Date(parseInt(cookieExpiry, 10)).toUTCString();
  const latLong = `${newStore.geoLocation.latitude},${newStore.geoLocation.longitude}`;
  const { state, zipCode } = newStore.address;
  const normalizedZipCode = zipCode.split('-')[0];

  /** *
   * Add the dfp cookie for all users to capture dfp for edge cases
   */
  if (is365Customer && !Cookie.get('sys_client_dfp')) {
    const { PersistentLoginUtil } = await import('@nuxt-common/util/app/utils/persistentLogin/index.js');
    Cookie.set('sys_client_dfp', PersistentLoginUtil.generateFingerPrint(), null, { expires });
  }

  if (Cookie.get('CSL') && !forceUpdate) {
    return;
  }

  Cookie.set('CSL', newStore.locationNumber);
  Cookie.set('BOPSPICKUPSTORE', newStore.locationNumber, 'MISCGCs', {
    expires,
  });
  Cookie.set('USERLL', latLong, 'MISCGCs', {
    expires,
  });
  Cookie.set('USERPC', normalizedZipCode, 'MISCGCs', {
    expires,
  });
  Cookie.set('BTZIPCODE', normalizedZipCode, 'MISCGCs', {
    expires,
  });
  Cookie.set('USERST', state, 'MISCGCs', {
    expires,
  });
  Cookie.set('PDDST', state, 'MISCGCs', {
    expires,
  });
}

export const useNavPreferred = defineStore('navPreferred', {
  state: () => ({
    preferredStore: {
      // Store name for MEW if store is not selected
      name: defaultStoreName,
    },
    sddMessage: null,
    locatorSlideoutRendered: false,
    stores: [],
    initialized: false,
  }),
  getters: {
    [GET_STORE_OPERATION_INFO]: (state) => parseOperationHours(state.preferredStore),
    [GET_STORE_INFO]: (state) => {
      const storeOperationInfo = state[GET_STORE_OPERATION_INFO];
      const isStoreOpen = storeOperationInfo.operationState.toLowerCase() === storeOperationStates.OPEN;

      return {
        name: state.preferredStore.name,
        address: state.preferredStore.address,
        id: state.preferredStore.id,
        schedule: state.preferredStore.schedule,
        phoneNumber: state.preferredStore.phoneNumber,
        locationNumber: state.preferredStore.locationNumber,
        operationState: storeOperationInfo.operationState,
        operationHours: storeOperationInfo.operationHours,
        isOpen: isStoreOpen,
        sddMessage: state.sddMessage,
      };
    },
    [GET_ZIP_CODE]: (state) => state.preferredStore.address?.zipCode,
    [GET_SDD_MESSAGE]: (state) => state.sddMessage,
    [GET_STORE_NAME]: (state) => state.preferredStore.name || defaultStoreName,
    [IS_STORE_SELECTED]: function () {
      return this[GET_STORE_NAME] !== defaultStoreName;
    },
  },
  actions: {
    [SET_SDD_MESSAGE](payload) {
      this.sddMessage = payload;
    },
    [SET_PREFERRED_STORE](payload) {
      this.preferredStore = payload;
    },
    renderLocatorSlideout() {
      this.locatorSlideoutRendered = true;
    },
    setInitialized() {
      this.initialized = true;
    },
    async [FETCH_PREFERRED_STORE]() {
      const storageValue = useStorage('commonstorelocation', {});
      const misCookie = Cookie.get('MISCGCs');
      const csl = Cookie.get('CSL');
      const userPC = Cookie.get('USERPC', 'MISCGCs');
      const btZipcode = Cookie.get('BTZIPCODE', 'MISCGCs');
      const bopsPickupStore = Cookie.get('BOPSPICKUPSTORE', 'MISCGCs');

      if (Object.keys(storageValue.value).length && misCookie && csl && userPC && btZipcode && bopsPickupStore) {
        this[SET_PREFERRED_STORE](storageValue.value);
        await updatePreferredStoreCookies(storageValue.value);
        this.setInitialized();
        return;
      }

      const navViewType = useNavViewType();
      const navUser = useNavUser();

      const { fetchPreferredStore } = usePreferredStore();
      const { guid, storeInfo } = navUser.data;
      const { viewType } = navViewType;
      const { latLng } = storeInfo;
      const deviceType = viewTypeToDeviceType[viewType];

      const isLatLngAvailable = latLng && Object.keys(latLng).length;
      if (!isLatLngAvailable) {
        this.setInitialized();
        return;
      }

      try {
        const result = await fetchPreferredStore({
          latLng,
          deviceType,
          guid,
        });
        if (result?.preferredStore) {
          const newStore = result.preferredStore;
          const data = normalizePreferredStore(newStore);

          this[SET_PREFERRED_STORE](data);
          storageValue.value = data;
          await updatePreferredStoreCookies(newStore, true);
          dispatchNavigationStoreChange(); // needed to notify DnF component when we change store
        }
      } catch (e) {
        logError(e?.message);
      } finally {
        this.setInitialized();
      }
    },
    async [FETCH_SDD_MESSAGE](zipCode) {
      const { fetchSDDMessage } = usePreferredStore();

      try {
        const result = await fetchSDDMessage({ zipCode });
        const ssdMessage = result?.stores?.store[0]?.sddMessage;
        if (result && ssdMessage) {
          this[SET_SDD_MESSAGE](ssdMessage);
        }
      } catch (e) {
        logError(e?.message);
      }
    },
    toggleLocatorSlideout() {
      if (this.locatorSlideoutRendered) {
        const slideoutStore = useSlideoutStore();
        slideoutStore.toggle('locator-slideout');
      } else {
        this.renderLocatorSlideout();
      }
    },
  },
});
